import { faSearch, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import { startOfToday, endOfToday, subDays } from "date-fns";
import { useState, useMemo, useEffect } from "react";
import { SelectRangeEventHandler, DateRange } from "react-day-picker";
import { twJoin } from "tailwind-merge";

import { DateRangePicker } from "src/base-components/DateRangePicker";
import {
  AccordionRoot,
  EditorAccordionItem,
} from "src/base-components/EditorAccordionItem";
import { TableComp } from "src/base-components/Table";
import { EventFilterRequest } from "src/clients/features-retrieve";
import { EmptyState } from "src/design-system/EmptyState";
import { useSidepane } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { getTableStoreKey } from "src/eventsCatalogue/EventsCatalogue";
import { EventsFilterDropdown } from "src/eventsCatalogue/EventsFilterDropdown";
import {
  useEventSchema,
  useEvents,
  useEventSchemas,
  useEventFilters,
  DeserializedEvent,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EventBlockProps = {
  eventType: string;
  isOpen: boolean;
  entityId: string;
  displayName: string;
};

const EventBlock: React.FC<EventBlockProps> = ({
  displayName,
  eventType,
  isOpen,
  entityId,
}) => {
  const [filters, setFilters] = useState<Record<string, string | null>>({});
  const [environment] = useEnvironment();
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const { workspace } = useWorkspaceContext();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow(
      {
        from: subDays(startOfToday(), 7),
        to: endOfToday(),
      },
      `events-time-window-${eventType}`,
    );

  const { data: schema, isLoading: isLoadingSchema } = useEventSchema(
    isOpen ? eventType : "",
  );

  const showFilterableEvents =
    isFeatureFlagEnabled(FEATURE_FLAGS.filterableEvents) &&
    featuresEventsEnabled;

  const {
    triggerFilters,
    clearFilters,
    filteredEvents,
    executionToken,
    isPending: isFilterPending,
    error: filterError,
  } = useEventFilters(eventType);

  const {
    data: events,
    isLoading: isEventsLoading,
    isRefetching: isRefetchingEvents,
  } = useEvents(
    {
      eventType: isOpen ? eventType : "",
      timeWindow: {
        from: timeWindow.from.toISOString(),
        to: timeWindow.to.toISOString(),
      },
      entityId,
      environment,
      filters,
    },
    {
      enabled: !executionToken,
    },
  );

  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const { resourceId: selectedEventId, toggleSidepane } = useSidepane("event");

  const memoizedColumns = useMemo(() => {
    return schema
      ? getColumns(schema, environment, entitiesSchemasData, {
          filters,
          onFilterChange: setFilters,
        })
      : [];
  }, [schema, entitiesSchemasData, filters, environment]);

  const onTimeWindowChange: SelectRangeEventHandler = (
    range: DateRange | undefined,
  ) => {
    if (range?.from && range?.to) {
      onDateRangePickerChange(range);
    }
  };

  const handleTriggerFilters = async (params: EventFilterRequest) => {
    await triggerFilters({
      ...params,
      entity_id: entityId,
    });
  };

  const headerContent = (
    <div
      className={twJoin(
        "h-13 content-center rounded-lg rounded-l-none border border-gray-200 bg-white",
        isOpen && "rounded-b-none",
      )}
    >
      <div
        className={`flex items-center ${showFilterableEvents ? "" : "gap-x-2 pr-4"}`}
      >
        <DateRangePicker
          disabled={isFilterDropdownOpen}
          suffixIcon={!isFilterDropdownOpen}
          usePredefinedDates={!isFilterDropdownOpen}
          value={dateRangePickerValue}
          borderless
          onChange={onTimeWindowChange}
        />
        {showFilterableEvents && (
          <EventsFilterDropdown
            dateRangeValue={dateRangePickerValue}
            eventType={eventType}
            executionToken={executionToken}
            onClearFilters={clearFilters}
            onDateRangeChange={onTimeWindowChange}
            onOpenChange={setIsFilterDropdownOpen}
            onTriggerFilters={handleTriggerFilters}
          />
        )}
      </div>
    </div>
  );

  const isLoading =
    (executionToken ? isFilterPending : isEventsLoading) ||
    isLoadingSchema ||
    isRefetchingEvents;

  // Use filtered events when filters are active, otherwise use regular events
  const displayEvents =
    (executionToken ? (filteredEvents as DeserializedEvent[]) : events) ?? [];

  const renderEventsContent = () => {
    // Show filter error if present
    if (executionToken && filterError) {
      return (
        <EmptyState
          actionButton={{
            text: "Clear filters",
            onClick: clearFilters,
            icon: faTrashAlt,
          }}
          dataLoc={`${eventType}-filter-error-state`}
          description={
            filterError instanceof Error
              ? filterError.message
              : "No data found for the selected filters. Please adjust the filters, and try running the query again."
          }
          headline="No events found"
          icon={faSearch}
        />
      );
    }

    if (!isLoading && displayEvents.length === 0) {
      return (
        <EmptyState
          dataLoc={`${eventType}-empty-state`}
          description="Try adjusting the filter to find what you are looking for"
          headline="No events found"
          icon={faSearch}
        />
      );
    }

    return (
      <div className="relative flex h-[364px] w-full overflow-x-auto">
        <TableComp
          columns={memoizedColumns}
          data={displayEvents}
          dataLoc={`${eventType}-events-table`}
          frameClassName="w-full"
          isLoading={isLoading}
          rowPropsGetter={(row) => ({
            onClick: () => {
              toggleSidepane({
                id: row.original.payload.id,
                eventType: eventType,
              });
            },
            classNameOverrides: twJoin(
              row.original.payload.id === selectedEventId
                ? "selected bg-gray-50"
                : "hover:bg-gray-50",
              "cursor-pointer",
            ),
          })}
          uiStoreKey={getTableStoreKey(eventType)}
          variant="compact"
        />
      </div>
    );
  };

  return (
    <EditorAccordionItem
      className="decideScrollbar overflow-hidden p-4 pt-3"
      contentWrapperClassName="bg-white border-b border-x border-gray-200 rounded-b-lg overflow-hidden"
      dataLoc={`${eventType}-group`}
      headerClassName={twJoin(
        "rounded-lg rounded-r-none border border-r-0 border-gray-200 bg-white transition-all",
        isOpen && "rounded-b-none",
      )}
      headerContent={headerContent}
      title={displayName}
      value={eventType}
    >
      {renderEventsContent()}
    </EditorAccordionItem>
  );
};

type EntityEventsProps = {
  entityId: string;
};

export const EntityEvents: React.FC<EntityEventsProps> = ({ entityId }) => {
  const { data: eventTypes, isLoading: isLoadingEventTypes } =
    useEventSchemas();
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  useEffect(() => {
    if (eventTypes && eventTypes.length > 0) {
      setAccordionValue([eventTypes[0].event_type]);
    }
  }, [eventTypes]);

  if (!eventTypes?.length && !isLoadingEventTypes) {
    return (
      <WhitePane noMinWidth>
        <EmptyState
          dataLoc="events-empty-state"
          description="There are no events found related to this entity yet."
          headline="No events found"
          icon={faSearch}
        />
      </WhitePane>
    );
  }

  return (
    <AccordionRoot
      className="flex flex-col gap-y-3"
      type="multiple"
      value={accordionValue}
      onValueChange={setAccordionValue}
    >
      {eventTypes?.map((eventType) => (
        <EventBlock
          key={eventType.event_type}
          displayName={eventType.display_name_plural}
          entityId={entityId}
          eventType={eventType.event_type}
          isOpen={accordionValue.includes(eventType.event_type)}
        />
      ))}
    </AccordionRoot>
  );
};

export const EventsTabContent = () => {
  const { id } = useParamsDecode<EntityViewParams>();

  return (
    <div className="p-5">
      <EntityEvents entityId={id} />
    </div>
  );
};
